/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import set from 'lodash/set';
import sortBy from 'lodash/sortBy';

import {
    Box,
    ContentBlock,
    CSRFacilityInformation,
    Heading,
    ProgressIndicator,
    ScrollToHelper,
    SecurityHelper,
    Spacer,
    withLDConsumer,
} from 'sarsaparilla';
import { withCompatRouter, COMPAT_ROUTER_PROP_TYPES } from 'sarsaparilla/legacy';
import { gnoment } from 'shared-ui/gnoment';
import { SendGAEvent } from 'shared-ui/utils/gaCommon';

import {
    OrderDetailsLayout,
    OrderDetailsTimerNotice,
    OrderDetailsSection,
    OrderDetailsSummary,
} from 'shared-ui/components/OrderDetailsLayout';
import GroupDetailHOC from 'ui-permit/shared-components/registration/GroupDetailNew';
import {
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_SAVE_PROFILE_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
} from 'ui-permit/shared-components/constants/propPaths';
import { INVENTORY_PERMIT } from 'ui-field-sales/src/constants/types';
import { updateAccount } from 'ui-account/src/actions/profileActions';
import * as sharedActions from 'ui-permit/shared-components/actions/actions';

import GroupMemberDetail from '../components/registration/GroupMemberDetail';
import NeedToKnow from '../components/registration/NeedToKnow';
import ReservationDetail from '../components/registration/ReservationDetail';
import * as pActions from '../actions/permit';
import {
    calculateEntrancesExits,
    convertIssuanceToGroupMembersObj,
    equipmentTypes,
    getCanadaEntryPointsByPermitType,
    getNewId,
    hasLotteryPreferenceDateDivisionGroupSize,
    issuanceStatuses,
    readRuleValue,
} from '../utils/permitUtilsNew';
import ErrorBanner from '../components/registration/ErrorBanner';
import PermitTripDates from '../components/registration/PermitTripDates';
import * as types from '../constants';
import ValidationError from '../components/registration/ValidationError';
import { setGroupDetails, computeWeekRange } from '../utils/issuanceUtils';
import CooperatorDetails from '../components/registration/CooperatorDetails';
import GuideDetails from '../components/registration/GuideDetails';
import NonProfitOrg from '../components/registration/NonProfitOrg';
import CanadaTravel from '../components/registration/CanadaTravel';
import WatercraftList from '../components/registration/WatercraftList';
import { selectors as sharedSelectors } from '../reducers/shared';

const PROGRESS_STEPS = ['Order Details', 'Review Cart', 'Payment', 'Confirmation'];

const WATERCRAFT_PROP_PATH = 'watercraft';

const dataBindings = {
    [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'groupLeader.home_address',
    [GROUP_DETAIL_PROP_PATH]: 'groupLeader',

    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'address2',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
    [GROUP_DETAIL_COUNTRY_PROP_PATH]: 'country',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'cell_phone',
    [GROUP_DETAIL_SAVE_PROFILE_PROP_PATH]: 'save_profile',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'state',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'zip_code',
};

const GroupDetailNew = GroupDetailHOC(PropTypes.shape({}), dataBindings);

class PermitRegistrationPage extends React.Component {
    static propTypes = {
        router: COMPAT_ROUTER_PROP_TYPES,
        issuance: PropTypes.shape({
            animals: PropTypes.arrayOf(PropTypes.shape({})),
            application_id: PropTypes.string,
            cooperator_id: PropTypes.string,
            bwcaw_num_days_canada: PropTypes.number,
            division_id: PropTypes.string,
            end_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
            equipments: PropTypes.arrayOf(PropTypes.shape({})),
            guides: PropTypes.arrayOf(PropTypes.shape({})),
            group_info: PropTypes.arrayOf(PropTypes.shape({})),
            id: PropTypes.string,
            is_commercially_guided: PropTypes.bool,
            is_nonprofit_org: PropTypes.bool,
            is_visiting_canada: PropTypes.bool,
            lottery_id: PropTypes.string,
            lottery_preferences: PropTypes.arrayOf(PropTypes.shape({})),
            need_to_know_checked: PropTypes.bool,
            order_id: PropTypes.string,
            org_name: PropTypes.string,
            org_type: PropTypes.string,
            permit_id: PropTypes.string,
            permit_type: PropTypes.string,
            pickup_location_id: PropTypes.string,
            start_date: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
            status: PropTypes.number,
            transaction_ids: PropTypes.arrayOf(PropTypes.string),
            exit_location_id: PropTypes.string,
        }).isRequired,
        cartError: PropTypes.shape({}),
        cooperator: PropTypes.shape({}),
        dispatch: PropTypes.func,
        entryDate: PropTypes.shape({}),
        fetchError: PropTypes.shape({}),
        filters: PropTypes.shape({}),
        issuanceError: PropTypes.shape({}),
        issuanceList: PropTypes.array,
        issuanceLoading: PropTypes.bool,
        permit: PropTypes.shape({
            description: PropTypes.oneOfType([
                PropTypes.shape({}),
                PropTypes.arrayOf(PropTypes.shape({})),
            ]),
            id: PropTypes.string,
            name: PropTypes.string,
            type: PropTypes.number,
            entrances: PropTypes.arrayOf(PropTypes.shape({})),
            divisions: PropTypes.shape({}),
            recareas: PropTypes.arrayOf(PropTypes.string),
            components: PropTypes.shape({
                animal_list: PropTypes.bool,
                entry_exit_points: PropTypes.bool,
                group_with_fees_and_dates: PropTypes.bool,
                snowmobile: PropTypes.bool,
                watercraft_list: PropTypes.bool,
            }),
            validations: PropTypes.arrayOf(PropTypes.shape({})),
        }),
        permitType: PropTypes.string,
        updateIssuanceResult: PropTypes.string,
        updatedIssuance: PropTypes.shape({}),
        validation: PropTypes.shape({
            summary: PropTypes.arrayOf(PropTypes.shape({})),
        }),
        imageUrl: PropTypes.string,
        flags: PropTypes.object,
    };

    static defaultProps = {
        entryDate: gnoment().utc(),
    };

    constructor(props) {
        super(props);

        this.state = {
            entryPointOptions: [],
            groupDetail: {
                groupLeaderType: 'primary-leader',
                groupLeader: SecurityHelper.getAccount(),
                otherLeader: {
                    email: '',
                },
                altLeaders: [],
            },
            groupMembers: [],
            launchDetail: {
                entryDate: null,
                exitDate: null,
                entryLocationId: '',
                exitLocationId: '',
                isExtended: false,
            },
            isGuided: null,
            guideDetails: {},
            isNonProfitOrg: null,
            orgType: null,
            orgName: null,
            isCanadaTravel: null,
            daysInCanada: null,
            maxMemberQuota: null,
            phValidations: {},
            displayError: false,
            division: {},
            watercrafts: [],
            vehicles: [],
            maxWatercraftQuota: null,
            validation: {},
            needToKnowChecked: false,
        };

        this.errorsRef = React.createRef();
    }

    componentDidMount() {
        const { reservationIds } = this.props.router.params;
        // fetch first issuance from the list of reservation ids and initialize
        // this page allows bulk editing of "initial" issuances (if there are multiple)
        const firstReservationId = reservationIds.split(',')[0];
        const issuanceId = firstReservationId.split(types.RES_ID_DELIMITER)[0];
        const permitId = types.BOUNDARY_WATERS_PERMIT_ID;

        this.props.dispatch(sharedActions.fetchReservationSummaryImageUrl(permitId));
        this.props.dispatch(pActions.fetchPermit(permitId)).then(() => {
            let recId = 0;
            if (this.props.permit.recareas && this.props.permit.recareas[0]) {
                recId = this.props.permit.recareas[0];
                this.props.dispatch(pActions.fetchRecarea(recId));
            }

            this.props
                .dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId))
                .then(() => {
                    const issuance = this.props.issuance;
                    let newGroup = [];

                    if (
                        issuance.status === issuanceStatuses.get(types.PERMIT_PRELIMINARY)
                    ) {
                        // include anything here that can be loaded from the quota filters
                        this.handleLaunchDetailChange(
                            this.convertIssuanceToLaunchDetailObj(issuance)
                        );
                        newGroup = convertIssuanceToGroupMembersObj(issuance.group_info);
                        this.handleGroupMemberChange(newGroup);
                        this.handleWatercraftsChange(
                            this.convertIssuanceToWatercraftObj(issuance.equipments)
                        );
                        this.handleVehiclesChange(
                            this.convertIssuanceToVehicleObj(issuance.equipments)
                        );
                        this.handleGroupDetailChange(
                            this.convertIssuanceToGroupDetailObj(
                                issuance,
                                this.state.groupDetail
                            )
                        );
                    } else if (
                        issuance.status ===
                            issuanceStatuses.get(types.PERMIT_IN_PROGRESS) ||
                        issuance.status === issuanceStatuses.get(types.LOTTERY_WON) ||
                        issuance.status === issuanceStatuses.get(types.PERMIT_COMPLETE) ||
                        issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED)
                    ) {
                        this.handleLaunchDetailChange(
                            this.convertIssuanceToLaunchDetailObj(issuance)
                        );
                        this.handleWatercraftsChange(
                            this.convertIssuanceToWatercraftObj(issuance.equipments)
                        );
                        this.handleVehiclesChange(
                            this.convertIssuanceToVehicleObj(issuance.equipments)
                        );
                        newGroup = this.loadGroupMembers(
                            this.props.permit,
                            issuance,
                            this.state.launchDetail
                        );
                        this.handleGroupDetailChange(
                            this.convertIssuanceToGroupDetailObj(
                                issuance,
                                this.state.groupDetail
                            )
                        );
                    }

                    this.setState({
                        division: issuance.division_id
                            ? this.props.permit.divisions[issuance.division_id]
                            : this.getDivision(),
                        entryPointOptions: this.getEntryPointOptions(),
                    });

                    if (issuance.cooperator_id) {
                        this.props.dispatch(
                            pActions.fetchCooperator(permitId, issuance.cooperator_id)
                        );
                    }

                    if (newGroup.length === 0) {
                        const newGroupMembers = [
                            {
                                id: 1,
                                removable: false,
                                fName: this.state.groupDetail.groupLeader.first_name,
                                mName: this.state.groupDetail.groupLeader.middle_initial,
                                lName: this.state.groupDetail.groupLeader.last_name,
                                passholderType: types.ADULT_FEE_TYPE,
                                isLeader: true,
                            },
                        ];
                        if (this.showFeesAndDates()) {
                            newGroupMembers[0].startDate = issuance.start_date;
                        }
                        this.setState({ groupMembers: newGroupMembers });
                    }

                    let guideDetails = get(issuance.guides, '0');
                    if (!guideDetails) {
                        guideDetails = {};
                    }

                    const newLaunchDetail = this.state.launchDetail;
                    if (issuance.start_date) {
                        newLaunchDetail.entryDate = gnoment(issuance.start_date).utc();
                    } else {
                        newLaunchDetail.entryDate = gnoment(this.props.entryDate).utc();
                    }

                    if (issuance.end_date) {
                        newLaunchDetail.exitDate = gnoment(issuance.end_date).utc();
                    }

                    newLaunchDetail.exitLocationId = issuance.exit_location_id;

                    this.setState({
                        launchDetail: newLaunchDetail,
                        isGuided: issuance.is_commercially_guided,
                        guideDetails: {
                            guideFirstName: guideDetails.first_name,
                            guideLastName: guideDetails.last_name,
                            guideNumber: guideDetails.passholder_id,
                        },
                        isNonProfitOrg: issuance.is_nonprofit_org,
                        orgType: issuance.org_type,
                        orgName: issuance.org_name,
                        isCanadaTravel: issuance.is_visiting_canada,
                        daysInCanada: issuance.bwcaw_num_days_canada,
                        needToKnowChecked: issuance.need_to_know_checked,
                    });
                });
        });
        this.props.dispatch(pActions.fetchCart());

        const issuanceIdList = reservationIds
            .split(',')
            .map((reservationId) => reservationId.split(types.RES_ID_DELIMITER)[0]);
        if (issuanceIdList.length > 1) {
            this.props.dispatch(
                pActions.fetchUnpaidIssuanceList(issuanceIdList, permitId)
            );
        } else {
            this.props.dispatch(pActions.clearUnpaidIssuanceList());
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const maxWatercraftQuota = readRuleValue(
            'QuotaUsageByWatercraft',
            nextProps.permit
        );
        if (maxWatercraftQuota) {
            this.setState({ maxWatercraftQuota: parseInt(maxWatercraftQuota, 10) });
        }

        if (nextProps.permit.entrances) {
            this.setState(
                calculateEntrancesExits(
                    nextProps.permit.entrances,
                    [],
                    nextProps.issuance.division_id,
                    nextProps.permit.divisions
                )
            );
        }

        const maxGroupSize = readRuleValue('MaxGroupSize', nextProps.permit);
        if (maxGroupSize) {
            this.setState({ maxMemberQuota: maxGroupSize });
        }
    }

    componentDidUpdate() {
        const validationErrorCount = get(this.props, 'validation.summary.length', 0);
        if (this.state.displayError && validationErrorCount === 0) {
            this.setState({ displayError: false });
        }
    }

    onChangeCooperator = async () => {
        // validate before saving so invalid form details are caught,
        // rather than silently persisted on the way to select-cooperator
        if (!this.isThisFormValid()) {
            this.setState({ displayError: true }, () => ScrollToHelper.scrollTo());
            return;
        }

        // save changes
        this.props.dispatch(pActions.clearIssuanceErrors());

        let items;
        if (get(this.props, 'issuanceList.length')) {
            items = get(this.props, 'issuanceList', []).map((issuance) =>
                this.createIssuanceObject(issuance, false)
            );
        } else {
            items = [this.createIssuanceObject(get(this.props, 'issuance'), false)];
        }
        this.props.dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        try {
            await pActions.updateItems(items);
        } catch (error) {
            if (error.response && error.response.data) {
                this.props.dispatch({
                    type: types.POST_ISSUANCE_ERROR,
                    payload: 'error while posting issuance data',
                    error: error.response.data,
                });
            }
            return;
        } finally {
            this.props.dispatch({
                type: types.SET_ISSUANCE_LOADING,
                issuanceLoading: false,
            });
        }

        // redirect
        const uri = `/permits/${types.BOUNDARY_WATERS_PERMIT_ID}/registration/${this.props.router.params.reservationIds}/select-cooperator`;
        this.props.router.navigate(uri);
    };

    onChangeStartDate = async () => {
        // validate before saving so invalid form details are caught,
        // rather than silently persisted on the way to change the date
        if (!this.isThisFormValid()) {
            this.setState({ displayError: true }, () => ScrollToHelper.scrollTo());
            return;
        }

        // save changes
        this.props.dispatch(pActions.clearIssuanceErrors());
        const issuanceListToUse = this.props.issuanceList
            ? this.props.issuanceList
            : [this.props.issuance];
        const items = issuanceListToUse.map((issuance) =>
            this.createIssuanceObject(issuance, false)
        );
        this.props.dispatch({
            type: types.SET_ISSUANCE_LOADING,
            issuanceLoading: true,
        });
        try {
            await pActions.updateItems(items);
        } catch (error) {
            if (error.response && error.response.data) {
                this.props.dispatch({
                    type: types.POST_ISSUANCE_ERROR,
                    payload: 'error while posting issuance data',
                    error: error.response.data,
                });
            }
            return;
        } finally {
            this.props.dispatch({
                type: types.SET_ISSUANCE_LOADING,
                issuanceLoading: false,
            });
        }

        const url = `/permits/${types.BOUNDARY_WATERS_PERMIT_ID}/registration/detailed-availability`;
        const searchParams = new URLSearchParams();

        if (this.props.router.params.reservationIds) {
            searchParams.set('reservationId', this.props.router.params.reservationIds);
        }

        if (this.state.division) {
            if (this.state.division.type === types.DIV_TYPE_OP) {
                searchParams.set('type', types.TYPE_OVERNIGHT_PADDLE);
            } else if (this.state.division.type === types.DIV_TYPE_OH) {
                searchParams.set('type', types.TYPE_OVERNIGHT_HIKE);
            } else if (this.state.division.type === types.DIV_TYPE_OM) {
                searchParams.set('type', types.TYPE_OVERNIGHT_MOTOR);
            }
        }

        if (this.state.launchDetail.entryDate) {
            searchParams.set(
                'date',
                this.state.launchDetail.entryDate.utc().startOf('day').toISOString()
            );
        }

        this.props.router.navigate(`${url}?${searchParams.toString()}`);
    };

    getDivision = () => {
        let divisionInIssuance;
        if (this.props.issuance.division_id) {
            divisionInIssuance =
                this.props.permit.divisions[this.props.issuance.division_id];
        }
        return divisionInIssuance;
    };

    getEquipments() {
        const equipmentInfo = {
            equipments: [],
        };
        this.state.watercrafts.forEach((c) => {
            equipmentInfo.equipments.push({
                type: parseInt(c.type, 10),
                qty: parseInt(c.amount, 10),
            });
        });
        this.state.vehicles.forEach((c) => {
            equipmentInfo.equipments.push({
                type: parseInt(c.type, 10),
                qty: 1,
                description: c.description,
                id: c.id,
                license_plate: c.license_plate,
                license_state: c.license_state,
                removable: c.removable,
            });
        });
        return equipmentInfo;
    }

    getGroupDetails() {
        const groupDetailInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            groupDetailInfo.group_leader_id = SecurityHelper.getAccount().account_id;
        }

        if (this.state.groupDetail.altLeaders) {
            const altGroupLeaders = [];
            for (let i = 0; i < this.state.groupMembers.length; i += 1) {
                if (this.state.groupMembers[i].alternateGroupLeaderId) {
                    altGroupLeaders.push({
                        first_name: this.state.groupMembers[i].fName,
                        last_name: this.state.groupMembers[i].lName,
                        middle_name: this.state.groupMembers[i].mName,
                        email: this.state.groupMembers[i].email,
                        alternate_group_leader_id:
                            this.state.groupMembers[i].alternateGroupLeaderId,
                    });
                }
            }
            groupDetailInfo.alt_group_leaders = altGroupLeaders;
        }
        return groupDetailInfo;
    }

    getMembers() {
        const memberInfo = {
            group_info: [],
        };
        this.state.groupMembers.forEach((m) => {
            memberInfo.group_info.push({
                first_name: m.fName,
                middle_name: m.mName,
                last_name: m.lName,
                remark: m.remark,
                start_date: m.startDate,
                end_date: m.endDate,
                fee_type: m.passholderType,
                passholder_id: m.passholderId,
                is_leader: m.isLeader,
                alternate_group_leader_id: m.alternateGroupLeaderId,
                email: m.email,
            });
        });
        return memberInfo;
    }

    getStatus() {
        if (this.props.issuance.status) {
            if (
                this.props.issuance.status !==
                issuanceStatuses.get(types.PERMIT_PRELIMINARY)
            ) {
                return this.props.issuance.status;
            }
        }
        return issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
    }

    loadGroupMembers = (permit, issuance, launchDetail) => {
        let groupMembers = issuance.group_info;
        let maxGroupSize = readRuleValue('MaxGroupSize', permit);
        // if processing lottery won application with group size data
        if (
            issuance.status === issuanceStatuses.get(types.LOTTERY_WON) &&
            hasLotteryPreferenceDateDivisionGroupSize(permit, issuance.division_id)
        ) {
            const groupSize = get(issuance, 'selected_lottery_preference.group_max_size');
            if (groupSize) {
                groupMembers = setGroupDetails(groupSize, launchDetail);
                maxGroupSize = groupSize;
            }
            // Set the upper bounds on number of group members that can be selected
            this.setState({ maxMemberQuota: parseInt(maxGroupSize, 10) });
        }
        const newGroup = convertIssuanceToGroupMembersObj(groupMembers);
        this.handleGroupMemberChange(newGroup);
        return newGroup;
    };

    isThisFormValid() {
        const { validation } = this.props;
        const { groupLeader } = this.state.groupDetail;
        const reservationId = `${this.props.issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;

        // map errors to new GroupDetails HOC
        const requiredFields = [
            'email',
            'first_name',
            'last_name',
            'cell_phone',
            'home_address.address1',
            'home_address.city',
            'home_address.country',
            'home_address.state',
            'home_address.zip_code',
        ];
        const phValidations = {};
        requiredFields.forEach((path) => {
            phValidations[`${reservationId}_groupLeader.${path}`] = !get(
                groupLeader,
                path
            );
            phValidations[`${reservationId}_groupLeader.${path}_req`] = !get(
                groupLeader,
                path
            );
        });

        this.setState({ phValidations });
        const isGroupLeaderValid = !requiredFields.some(
            (path) => !get(groupLeader, path)
        );

        // run validations on all form fields before saving and potentially leaving the page,
        // which could cause invalid form details to be silently persisted and cause
        // stuck errors on the new page
        const isWatercraftValid = this.validateWatercrafts();
        const isGuideDetailsValid = this.validateGuideDetails();
        const isCanadaTravelValid = this.validateCanadaTravel();
        const isNonProfitOrgValid = this.validateNonProfitOrg();
        const isNeedToKnowValid = this.validateNeedToKnow();
        const isTripDatesValid = this.validateTripDates();
        const isGroupMembersValid = this.validateGroupMembers();

        const nonManualErrors = validation.summary.filter(
            (err) => !this.isManuallyValidatedPath(err.path)
        );

        return (
            nonManualErrors.length === 0 &&
            isGroupLeaderValid &&
            isWatercraftValid &&
            isGuideDetailsValid &&
            isCanadaTravelValid &&
            isNonProfitOrgValid &&
            isNeedToKnowValid &&
            isTripDatesValid &&
            isGroupMembersValid
        );
    }

    validateWatercrafts() {
        // flags watercraft entries with a missing type or no quantity set
        // returns true only when every watercraft has a valid type and quantity
        const { issuance, validation } = this.props;
        const watercrafts = this.state.watercrafts || [];
        const pathPrefix = `${issuance.id}_${WATERCRAFT_PROP_PATH}[`;
        let allValid = true;

        // reset tracking of previously flagged watercraft entries and
        // re-add only for the rows that are currently invalid
        get(validation, 'summary', [])
            .filter((err) => err.path.startsWith(pathPrefix))
            .forEach((err) =>
                this.props.dispatch(pActions.formValidationChange(err.path, null))
            );

        watercrafts.forEach((watercraft, index) => {
            const type = parseInt(watercraft.type, 10) || 0;
            const qty = parseInt(watercraft.amount, 10) || 0;

            if (type === 0) {
                allValid = false;
                this.props.dispatch(
                    pActions.formValidationChange(`${pathPrefix}${index}].type`, {
                        message: 'Watercraft Type is required.',
                    })
                );
            }
            if (qty === 0) {
                allValid = false;
                this.props.dispatch(
                    pActions.formValidationChange(`${pathPrefix}${index}].qty`, {
                        message: 'Watercraft Quantity is required.',
                    })
                );
            }
        });

        return allValid;
    }

    getValidationPath(path) {
        return `${this.props.issuance.id}_${path}`;
    }

    updateValidationError(path, isInvalid, message) {
        this.props.dispatch(
            pActions.formValidationChange(path, isInvalid ? { message } : null)
        );
        return !isInvalid;
    }

    isManuallyValidatedPath(path) {
        if (!path) {
            return false;
        }

        const issuanceIdPrefix = `${this.props.issuance.id}_`;

        return (
            path.includes(`_${WATERCRAFT_PROP_PATH}[`) ||
            path === `${issuanceIdPrefix}need_to_know` ||
            path.startsWith(`${issuanceIdPrefix}guide_details.`) ||
            path === `${issuanceIdPrefix}is_visiting_canada` ||
            path === `${issuanceIdPrefix}bwcaw_num_days_canada` ||
            path === `${issuanceIdPrefix}entry_location_id` ||
            path === `${issuanceIdPrefix}org_type` ||
            path === `${issuanceIdPrefix}org_name` ||
            path === `${issuanceIdPrefix}end_date` ||
            (path.startsWith(`${issuanceIdPrefix}group_info[`) &&
                (path.endsWith('.first_name') || path.endsWith('.last_name')))
        );
    }

    validateGuideDetails() {
        const { isGuided, guideDetails } = this.state;
        const hasText = (value) => `${value || ''}`.trim().length > 0;
        let isValid = true;

        if (
            !this.updateValidationError(
                this.getValidationPath('guide_details.is_commercially_guided'),
                typeof isGuided !== 'boolean',
                'Commercially Guided is required.'
            )
        ) {
            isValid = false;
        }

        const guideFieldChecks = [
            {
                path: 'guide_details.guides[0].first_name',
                value: get(guideDetails, 'guideFirstName'),
                message: 'Guide First Name is required.',
            },
            {
                path: 'guide_details.guides[0].last_name',
                value: get(guideDetails, 'guideLastName'),
                message: 'Guide Last Name is required.',
            },
            {
                path: 'guide_details.guides[0].passholder_id',
                value: get(guideDetails, 'guideNumber'),
                message: 'Guide Number is required.',
            },
        ];

        guideFieldChecks.forEach((field) => {
            const isInvalid = isGuided === true && !hasText(field.value);
            if (
                !this.updateValidationError(
                    this.getValidationPath(field.path),
                    isInvalid,
                    field.message
                )
            ) {
                isValid = false;
            }
        });

        return isValid;
    }

    validateCanadaTravel() {
        const { isCanadaTravel, daysInCanada, launchDetail, entryPointOptions } =
            this.state;
        const isEntryPointEnabled = entryPointOptions.length > 0;
        let isValid = true;

        if (
            !this.updateValidationError(
                this.getValidationPath('is_visiting_canada'),
                typeof isCanadaTravel !== 'boolean',
                'Canada Travel is required.'
            )
        ) {
            isValid = false;
        }

        const isDaysInvalid =
            isCanadaTravel === true && !(parseInt(daysInCanada, 10) > 0);
        if (
            !this.updateValidationError(
                this.getValidationPath('bwcaw_num_days_canada'),
                isDaysInvalid,
                'Number of Nights in Canada is required.'
            )
        ) {
            isValid = false;
        }

        const isEntryPointInvalid =
            isCanadaTravel === true &&
            isEntryPointEnabled &&
            !`${get(launchDetail, 'entryLocationId', '')}`.trim();
        if (
            !this.updateValidationError(
                this.getValidationPath('entry_location_id'),
                isEntryPointInvalid,
                'Entry Point is required.'
            )
        ) {
            isValid = false;
        }

        return isValid;
    }

    validateNonProfitOrg() {
        const { isNonProfitOrg, orgType, orgName } = this.state;
        const hasText = (value) => `${value || ''}`.trim().length > 0;
        let isValid = true;

        if (
            !this.updateValidationError(
                this.getValidationPath('org_type'),
                isNonProfitOrg === true && !hasText(orgType),
                'Non-Profit Organization Type is required.'
            )
        ) {
            isValid = false;
        }

        if (
            !this.updateValidationError(
                this.getValidationPath('org_name'),
                isNonProfitOrg === true && !hasText(orgName),
                'Non-Profit Organization Name is required.'
            )
        ) {
            isValid = false;
        }

        return isValid;
    }

    validateNeedToKnow() {
        return this.updateValidationError(
            this.getValidationPath('need_to_know'),
            !this.state.needToKnowChecked,
            'Need-to-Know must be checked.'
        );
    }

    validateTripDates() {
        const div = this.props.permit.divisions[this.props.issuance.division_id];
        const hideTripDatesSection =
            div && (div.type === types.DIV_TYPE_DM || div.type === types.DIV_TYPE_DC);
        const entryOnly = readRuleValue('ShowEntryDateOnly', this.props.permit);

        if (hideTripDatesSection || entryOnly) {
            this.updateValidationError(this.getValidationPath('end_date'), false, '');
            return true;
        }

        return this.updateValidationError(
            this.getValidationPath('end_date'),
            !this.state.launchDetail.exitDate,
            'Exit Date is required.'
        );
    }

    validateGroupMembers() {
        const { validation } = this.props;
        const groupMembers = Array.isArray(this.state.groupMembers)
            ? this.state.groupMembers
            : [];
        const pathPrefix = this.getValidationPath('group_info[');
        const hasText = (value) => `${value || ''}`.trim().length > 0;
        let isValid = true;

        // clear stale first/last name errors from removed members
        get(validation, 'summary', [])
            .filter(
                (err) =>
                    err.path.startsWith(pathPrefix) &&
                    (err.path.endsWith('.first_name') || err.path.endsWith('.last_name'))
            )
            .forEach((err) =>
                this.props.dispatch(pActions.formValidationChange(err.path, null))
            );

        groupMembers.forEach((member, index) => {
            const memberNumber = index + 1;

            if (
                !this.updateValidationError(
                    this.getValidationPath(`group_info[${index}].first_name`),
                    !hasText(get(member, 'fName')),
                    `Group Member #${memberNumber} First Name is required.`
                )
            ) {
                isValid = false;
            }

            if (
                !this.updateValidationError(
                    this.getValidationPath(`group_info[${index}].last_name`),
                    !hasText(get(member, 'lName')),
                    `Group Member #${memberNumber} Last Name is required.`
                )
            ) {
                isValid = false;
            }
        });

        return isValid;
    }

    getPermitHolder() {
        // returns an object with keys 'permit_holder' and 'permit_holder_address'
        // for use when sending issuance object away
        const permitHolderInfo = {};
        if (this.state.groupDetail.groupLeaderType === 'primary-leader') {
            const {
                first_name,
                last_name,
                email,
                cell_phone: phone,
            } = get(this.state.groupDetail, 'groupLeader', {});
            const homeAddress = get(
                this.state.groupDetail,
                'groupLeader.home_address',
                {}
            );
            const {
                address1: street_address1,
                address2: street_address2,
                city,
                state: state_code,
                zip_code: postal_code,
            } = homeAddress;
            permitHolderInfo.permit_holder = { first_name, last_name, email, phone };
            permitHolderInfo.permit_holder_address = {
                street_address1,
                street_address2,
                city,
                state_code,
                postal_code,
            };
        }
        return permitHolderInfo;
    }

    focusErrorNotification = () => {
        if (this.errorsRef.current) {
            this.errorsRef.current.focus();
        }
    };

    convertIssuanceToGroupDetailObj(issuance, groupDetail) {
        // this takes issuance fields and builds an object used for local representation
        // this is the problem method, that is cross referenced.
        // parameter changes are the problem.
        const alternateLeaders = issuance.alt_group_leaders;
        const permitHolder = issuance.permit_holder;
        const permitHolderAddress = issuance.permit_holder_address;
        const result = groupDetail;

        if (permitHolder.first_name) {
            // get the 'groupLeader' from the Individual/Address objects
            result.groupLeader = {
                first_name: permitHolder.first_name,
                last_name: permitHolder.last_name,
                email: permitHolder.email,
                cell_phone: permitHolder.phone,
                user_id: permitHolder.user_id,
                home_address: {
                    address1: permitHolderAddress.street_address1,
                    address2: permitHolderAddress.street_address2,
                    city: permitHolderAddress.city,
                    country: permitHolderAddress.country_code,
                    state: permitHolderAddress.state_code,
                    zip_code: permitHolderAddress.postal_code,
                },
            };
        } else {
            // get the group leader info from current session account
            result.groupLeader = SecurityHelper.getAccount();
        }

        if (alternateLeaders) {
            const altLeaders = [];
            for (let index = 0; index < alternateLeaders.length; index += 1) {
                const leader = alternateLeaders[index];
                const altLeader = {
                    key: getNewId(),
                    fName: leader.first_name ? leader.first_name : '',
                    mName: leader.middle_name ? leader.middle_name : '',
                    lName: leader.last_name ? leader.last_name : '',
                    phone: '',
                    email: leader.email,
                    alternateGroupLeaderId: leader.alternate_group_leader_id
                        ? leader.alternate_group_leader_id
                        : '',
                    is_verified: leader.is_verified,
                };
                altLeaders.push(altLeader);
            }
            result.altLeaders = altLeaders;
        }
        return result;
    }

    convertIssuanceToLaunchDetailObj(issuance) {
        const item = {
            entryLocationId: issuance.entry_location_id,
            exitLocationId: issuance.exit_location_id,
            isExtended: issuance.is_extended,
        };
        if (issuance.start_date) {
            item.entryDate = gnoment(issuance.start_date).utc();
        }
        if (issuance.end_date) {
            item.exitDate = gnoment(issuance.end_date).utc();
        }
        return item;
    }

    convertIssuanceToWatercraftObj(equipments) {
        const result = [];
        if (equipments) {
            for (const e of equipments) {
                if (parseInt(e.type, 10) !== equipmentTypes.get('Vehicle')) {
                    result.push({
                        type: e.type,
                        amount: e.qty,
                    });
                }
            }
        }
        return result;
    }

    convertIssuanceToVehicleObj(equipments) {
        const result = [];
        if (equipments) {
            for (const e of equipments) {
                if (parseInt(e.type, 10) === equipmentTypes.get('Vehicle')) {
                    result.push({
                        type: parseInt(e.type, 10),
                        qty: 1,
                        description: e.description,
                        id: e.id,
                        license_plate: e.license_plate,
                        license_state: e.license_state,
                        removable: e.removable,
                    });
                }
            }
        }
        return result;
    }

    handleGroupDetailChange = (e) => this.setState({ groupDetail: e });

    handleGroupMemberChange = (e) => {
        const altGroupLeaders = this.getGroupDetails().alt_group_leaders;
        const groupDetailToUpdate = this.state.groupDetail;
        groupDetailToUpdate.altLeaders = altGroupLeaders;

        this.setState({
            groupMembers: e,
            groupDetail: groupDetailToUpdate,
        });
    };

    handleWatercraftsChange = (e) => this.setState({ watercrafts: e });

    handleVehiclesChange = (e) => this.setState({ vehicles: e });

    handleNeedToKnowCheckboxChange = () => {
        this.setState(
            (prevState) => ({ needToKnowChecked: !prevState.needToKnowChecked }),
            () => {
                if (this.state.needToKnowChecked) {
                    this.scrollToReservationDetails();
                }
            }
        );
    };

    showFeesAndDates() {
        return this.props.permit.components
            ? this.props.permit.components.group_with_fees_and_dates
            : false;
    }

    formValidationChange = () => {
        // origin function signature formValidationChange(path, error)
        // TODO Need to revisit this validation. It is doing some processing in the reducer that is slowing down the entire registration page.
        // Removing this does not break anything as the validations will still run on the backend and prevent checkout.
        // return this.props.dispatch(pActions.formValidationChange(path, error));
    };

    getValidationErrorBullets(validation) {
        const summaries = sortBy(get(validation, 'summary', []), 'path');
        return summaries.map((summary) => {
            const { path, error } = summary;

            // Keep validation actions/state serializable; still render any
            // legacy element-shaped errors that may already exist.
            if (React.isValidElement(error)) {
                return React.cloneElement(error, { key: path });
            }

            const message =
                typeof error === 'string' ? error : get(error, 'message', null);

            if (message) {
                return <li key={path}>{message}</li>;
            }

            return <li key={path}>Please correct this field.</li>;
        });
    }

    getEntryPointOptions = () =>
        getCanadaEntryPointsByPermitType(this.props.issuance.permit_type).map(
            (entry) => ({
                label: entry,
                value: entry,
            })
        );

    scrollToReservationDetails = () => {
        ScrollToHelper.scrollToY(0);
    };

    handleIsGuidedChange = (value) => {
        this.setState({
            isGuided: value,
        });
    };

    handleGuideFirstNameChange = (value) => {
        const guideDetails = { ...this.state.guideDetails, guideFirstName: value };
        this.setState({ guideDetails });
    };

    handleGuideLastNameChange = (value) => {
        const guideDetails = { ...this.state.guideDetails, guideLastName: value };
        this.setState({ guideDetails });
    };

    handleGuideNumberChange = (value) => {
        const guideDetails = { ...this.state.guideDetails, guideNumber: value };
        this.setState({ guideDetails });
    };

    handleIsNonProfitOrgChange = (value) => {
        this.setState({
            isNonProfitOrg: value,
        });
    };

    handleOrgTypeChange = (value) => {
        this.setState({
            orgType: value,
        });
    };

    handleOrgNameChange = (value) => {
        this.setState({
            orgName: value,
        });
    };

    handleCanadaTravelChange = (value) => {
        this.setState({
            isCanadaTravel: value,
        });
    };

    handleCanadaDaysChange = (value) => {
        this.setState({
            daysInCanada: value,
        });
    };

    handleCanadaEntryPointChange = (value) => {
        this.setState((prevState) => ({
            launchDetail: {
                ...prevState.launchDetail,
                entryLocationId: value,
            },
        }));
    };

    handleLaunchDetailChange = (e) => {
        this.setState({ launchDetail: e });
    };

    isStartDateChangeAllowed() {
        const notPaidStatus =
            this.props.issuance.status ===
                issuanceStatuses.get(types.PERMIT_PRELIMINARY) ||
            this.props.issuance.status === issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
        let nonBulkType = false;
        const permitType = this.props.permitType;
        if (
            permitType === types.TYPE_OVERNIGHT_PADDLE ||
            permitType === types.TYPE_OVERNIGHT_HIKE ||
            permitType === types.TYPE_OVERNIGHT_MOTOR
        ) {
            nonBulkType = true;
        }
        return notPaidStatus && nonBulkType;
    }

    createIssuanceObject = (issuance, isUpdateStatus) => {
        // TODO find better way for mapping existing issuance to initItem (copying from fetched existing iss -> newly updating iss)
        const initItem = {
            permit_id: issuance.permit_id,
            division_id: issuance.division_id,
            permit_type: issuance.permit_type,
            lottery_id: issuance.lottery_id,
            application_id: issuance.application_id,
            transaction_ids: issuance.transaction_ids,
            lottery_preferences: issuance.lottery_preferences,
            order_id: issuance.order_id,
            order_date: issuance.order_date,
            user_id: SecurityHelper.getAccount().account_id,
            id: issuance.id,
            user_type: 1,
            acquisition_type: 1,
            start_date: issuance.start_date,
            end_date: this.state.launchDetail.exitDate,
            exit_location_id: this.state.launchDetail.exitLocationId,
            need_to_know_checked: this.state.needToKnowChecked,
            cooperator_id: issuance.cooperator_id,
            is_commercially_guided: this.state.isGuided,
            guides: [
                {
                    first_name: this.state.guideDetails.guideFirstName,
                    last_name: this.state.guideDetails.guideLastName,
                    passholder_id: this.state.guideDetails.guideNumber,
                },
            ],
            is_nonprofit_org: this.state.isNonProfitOrg,
            org_type: this.state.orgType,
            org_name: this.state.orgName,
            is_visiting_canada: this.state.isCanadaTravel,
            bwcaw_num_days_canada:
                this.state.isCanadaTravel === true ? this.state.daysInCanada : null,
            entry_location_id: this.state.isCanadaTravel
                ? this.state.launchDetail.entryLocationId
                : undefined,
            fee_overrides: issuance.fee_overrides,
            fee_override_justification: issuance.fee_override_justification,
        };
        if (isUpdateStatus) {
            initItem.status = this.getStatus();
        }

        if (!this.state.isGuided) {
            initItem.guides = null;
        }

        const groupDetail = this.getGroupDetails();
        const equipmentDetail = this.getEquipments();
        const memberDetail = this.getMembers();

        const permitHolder = this.getPermitHolder();

        return Object.assign(
            initItem,
            groupDetail,
            memberDetail,
            equipmentDetail,
            permitHolder
        );
    };

    addToCart = async ({ navigate = true } = {}) => {
        if (!this.isThisFormValid()) {
            this.setState({ displayError: true }, () => ScrollToHelper.scrollTo());
            return;
        }

        this.props.dispatch(pActions.clearIssuanceErrors());
        if (this.state.groupDetail.groupLeader.save_profile) {
            this.saveLeaderInfo();
        }
        const reservationIds = (this.props.router.params.reservationIds || '').split(',');

        if (reservationIds.length === 1) {
            // edit single issuance - check if fee changed, then redirect to cart
            const reservationId = reservationIds[0];
            const issuanceId = reservationId.split(types.RES_ID_DELIMITER)[0];
            const permitId = reservationId.split(types.RES_ID_DELIMITER)[1];

            await this.props.dispatch(pActions.fetchUnpaidIssuance(issuanceId, permitId));
            const item = this.createIssuanceObject(this.props.issuance, true);
            try {
                await this.props.dispatch(pActions.updateIssuance(item));
            } catch {
                this.focusErrorNotification();
                return;
            }

            switch (this.props.updateIssuanceResult) {
                case types.ISSUANCE_UPDATE_REQUIRES_PAYMENT:
                    if (navigate) window.location.assign('/cart');
                    return true;
                case types.ISSUANCE_UPDATE_REQUIRES_REFUND:
                    this.props.dispatch(
                        pActions.bypassToOrderConfirmation(
                            this.props.updatedIssuance,
                            this.props.permit
                        )
                    );
                    if (navigate) {
                        this.props.router.navigate(
                            `/permits/${this.props.permit.id}/confirmation`
                        );
                    }
                    return true;
                case types.ISSUANCE_UPDATE_SUCCESSFUL:
                    if (navigate) {
                        window.location.assign(
                            `/account/orders/${this.props.issuance.order_id}/reservations/${this.props.issuance.id}__${this.props.permit.id}`
                        );
                    }
                    return true;
                case undefined:
                    this.focusErrorNotification();
                    return;
                default:
                    if (navigate) window.location.assign('/cart');
                    return true;
            }
        } else {
            // bulk edit multiple issuances - this always redirects to cart since bulk editing only can be done on new, non purchased items only
            const items = this.props.issuanceList.map((issuance) =>
                this.createIssuanceObject(issuance, true)
            );
            this.props.dispatch({
                type: types.SET_ISSUANCE_LOADING,
                issuanceLoading: true,
            });
            try {
                await pActions.updateItems(items);
            } catch (error) {
                if (error.response && error.response.data) {
                    this.props.dispatch({
                        type: types.POST_ISSUANCE_ERROR,
                        payload: 'error while posting issuance data',
                        error: error.response.data,
                    });
                    this.focusErrorNotification();
                }
                return;
            } finally {
                this.props.dispatch({
                    type: types.SET_ISSUANCE_LOADING,
                    issuanceLoading: false,
                });
            }
            if (navigate) window.location.assign('/cart');
            return true;
        }
    };

    updateStateGroupDetail = (id, path, value) =>
        this.setState(({ groupDetail }) => ({
            groupDetail: set({ ...groupDetail }, path, value),
        }));

    removeFromCart = () => {
        const issuanceId = get(this.props.issuance, 'id');
        const permitId = get(this.props.permit, 'id');
        if (issuanceId && permitId) {
            const reservationId = `${issuanceId}${types.RES_ID_DELIMITER}${permitId}`;
            const reservationIds = this.props.router.params.reservationIds.split(',');
            if (reservationIds.length > 1) {
                this.props.dispatch(
                    pActions.cancelBulkReservation(
                        reservationIds,
                        this.props.router.navigate
                    )
                );
            } else {
                this.props.dispatch(
                    pActions.cancelReservation(reservationId, this.props.router.navigate)
                );
            }
        }
    };

    saveLeaderInfo = () => {
        const acct = this.createUpdatedAccountObject(SecurityHelper.getAccount());
        this.props.dispatch(updateAccount(acct));
    };

    createUpdatedAccountObject = (prevAcct) => {
        const { first_name, last_name, home_address, email, home_phone, cell_phone } =
            this.state.groupDetail.groupLeader;
        return {
            ...prevAcct,
            first_name,
            last_name,
            home_address,
            email,
            home_phone,
            cell_phone,
        };
    };

    renderGroupDetail() {
        const { issuance } = this.props;
        const { groupDetail } = this.state;
        const reservationId = `${issuance.id}${types.RES_ID_DELIMITER}${this.props.permit.id}`;

        return (
            <>
                <GroupDetailNew
                    displayValidationError
                    pActions={{
                        ...pActions,
                        formValidationChange: Function.prototype,
                        updatePendingReservation: this.updateStateGroupDetail,
                    }}
                    reservation={{
                        ...groupDetail,
                        sales_channel_type: 0,
                        id: reservationId,
                    }}
                    validation={this.state.phValidations}
                />
                <hr />
            </>
        );
    }

    renderTripDates() {
        const div = this.props.permit.divisions[this.props.issuance.division_id];
        if (div && (div.type === types.DIV_TYPE_DM || div.type === types.DIV_TYPE_DC)) {
            // do not show entry/exit dates for day use issuances
            return null;
        }

        let isDisabled =
            this.props.issuance.status !== issuanceStatuses.get(types.PERMIT_COMPLETE) &&
            this.props.issuance.status !==
                issuanceStatuses.get(types.PERMIT_PRELIMINARY) &&
            this.props.issuance.status !== issuanceStatuses.get(types.PERMIT_IN_PROGRESS);
        // check if issuance is from a lottery win, if so, check if dates should be locked/disabled from user input
        const isLotteryWon =
            this.props.issuance.status === issuanceStatuses.get(types.LOTTERY_WON);
        if (isLotteryWon) {
            isDisabled =
                get(
                    this.props.issuance,
                    'selected_lottery_preference.length_of_stay',
                    0
                ) > 0;
        }

        return (
            <PermitTripDates
                permit={this.props.permit}
                issuance={this.props.issuance}
                launchDetail={this.state.launchDetail}
                handleLaunchDetailChange={this.handleLaunchDetailChange}
                division={this.state.division}
                onChangeStartDate={(e) => {
                    e.preventDefault();
                    this.onChangeStartDate();
                }}
                isStartDateChangeAllowed={this.isStartDateChangeAllowed()}
                isDisabled={isDisabled}
                formValidationChange={this.formValidationChange}
                validation={this.props.validation}
                displayValidationError={this.state.displayError}
            />
        );
    }

    renderWatercraftList() {
        if (
            !this.props.permit ||
            !this.props.permit.components ||
            !this.props.permit.components.watercraft_list ||
            (this.state.division && this.state.division.type === 'OH')
        ) {
            return null;
        }
        return (
            <div>
                <WatercraftList
                    permit={this.props.permit}
                    issuance={this.props.issuance}
                    issuanceStartDate={this.props.issuance.start_date}
                    watercrafts={this.state.watercrafts}
                    handleWatercraftsChange={this.handleWatercraftsChange}
                    maxQuota={this.state.maxWatercraftQuota}
                    formValidationChange={this.formValidationChange}
                    validation={this.props.validation}
                    displayValidationError={this.state.displayError}
                />
                <hr />
            </div>
        );
    }

    isPermitIssued = (issuance) => {
        return [types.PERMIT_ISSUED, types.PERMIT_COMPLETE]
            .map((st) => issuanceStatuses.get(st))
            .includes(issuance.status);
    };

    isLoadingInitialContent = () => {
        const reservationIdsList = (this.props.router.params.reservationIds || '').split(
            ','
        );
        const firstReservationId = reservationIdsList[0];
        return (
            !this.props.permit?.id ||
            !this.props.issuance?.id ||
            !this.state.division.name ||
            firstReservationId !== `${this.props.issuance.id}__${this.props.permit.id}`
        );
    };

    renderTimeNotice = () => {
        // Permit complete does not have time limit to fill order details
        if (this.props.issuance.status === issuanceStatuses.get(types.PERMIT_COMPLETE)) {
            return null;
        }
        return <OrderDetailsTimerNotice />;
    };

    renderSummary = () => {
        const reservationIdsList = (this.props.router.params.reservationIds || '').split(
            ','
        );
        const permitCount = reservationIdsList.length;
        const disabledButtons =
            this.props.issuanceLoading ||
            this.props.issuance.status === issuanceStatuses.get(types.PERMIT_ISSUED);
        const modifiableStatus =
            this.props.issuance?.status === issuanceStatuses.get(types.PERMIT_ISSUED) ||
            this.props.issuance?.status === issuanceStatuses.get(types.PERMIT_COMPLETE) ||
            this.props.issuance?.status === issuanceStatuses.get(types.LOTTERY_WON);
        return (
            <OrderDetailsSummary
                isLoading={this.isLoadingInitialContent()}
                heading={`${this.state.division?.name || ''} (${
                    SecurityHelper.isCommercialAccount() ? 'Commercial' : 'Non-Commercial'
                })`}
                imageUrl={this.props.imageUrl}
                isWaitingForServerResponse={this.props.issuanceLoading}
                parentName={this.props.permit.name}
                isProceedToCartDisabled={disabledButtons}
                onProceedToCartClick={this.addToCart}
                proceedToCartButtonText="Proceed to Checkout"
                secondaryButtonText="Continue Shopping"
                onSecondaryButtonClick={this.continueShopping}
                showBuyNow={permitCount === 1 && !modifiableStatus}
                reservationId={permitCount === 1 ? reservationIdsList[0] : undefined}
            >
                <ReservationDetail
                    permit={this.props.permit}
                    division={this.state.division}
                    issuance={this.props.issuance}
                    issuanceList={this.props.issuanceList}
                    permitCount={permitCount}
                    entryDate={this.state.launchDetail.entryDate}
                    exitDate={this.state.launchDetail.exitDate}
                    groupMembers={this.state.groupMembers}
                    issuanceStatus={this.props.issuance.status}
                />
            </OrderDetailsSummary>
        );
    };

    render() {
        const maxAlternateLeaders = Number(
            readRuleValue('MaxAlternateLeaders', this.props.permit)
        );
        const { start, end } = computeWeekRange(
            gnoment(this.props.issuance.start_date).utc()
        );
        return (
            <div className="boundarywaters-order-details-page">
                {/* Fire the add_to_cart GA Event */}
                <SendGAEvent
                    eventType="add_to_cart"
                    itemCategory={INVENTORY_PERMIT}
                    facilityId={this.props.permit.id}
                    facilityName={this.props.permit.name}
                    //isDataLoading={!this.props.permit || !this.props.permit.id}
                />

                <Box background="gray-2" paddingY="sm">
                    <ProgressIndicator steps={PROGRESS_STEPS} activeStep={1} />
                </Box>
                <Spacer size="xl" />
                <ContentBlock>
                    <OrderDetailsLayout summaryElement={this.renderSummary()}>
                        <div className="rec-error-page-topper">
                            <ErrorBanner
                                errors={[
                                    this.props.issuanceError,
                                    this.props.fetchError,
                                    this.props.cartError,
                                ]}
                                innerRef={this.errorsRef}
                            />
                        </div>
                        <ValidationError
                            displayError={this.state.displayError}
                            toggle={() => this.setState({ displayError: false })}
                        >
                            {[
                                <p key="validation-error-0">
                                    Form has <b>{this.props.validation.summary.length}</b>{' '}
                                    validation errors. Please correct them before you can
                                    proceed to payment.
                                </p>,
                                <ul key="validation-errors-detail">
                                    {this.getValidationErrorBullets(
                                        this.props.validation
                                    )}
                                </ul>,
                            ]}
                        </ValidationError>
                        <Heading headingLevel={1} appearance="h3">
                            Order Details
                        </Heading>
                        {this.renderTimeNotice()}
                        <CSRFacilityInformation
                            inventory="Permit"
                            facility_id={this.props.permit.id}
                        />

                        <OrderDetailsSection
                            heading={this.props.permit.name}
                            onRemoveClick={
                                !this.isPermitIssued(this.props.issuance)
                                    ? this.removeFromCart
                                    : null
                            }
                            isLoading={this.isLoadingInitialContent()}
                        >
                            <form id="form-name" className="permit-content-main">
                                <fieldset
                                    disabled={
                                        this.props.issuance.status ===
                                        issuanceStatuses.get(types.PERMIT_ISSUED)
                                    }
                                >
                                    {this.renderGroupDetail()}
                                    {this.props.issuance.permit_type ===
                                        'DayUseMotor' && (
                                        <div>
                                            <div>
                                                <h2 className={'h5'}>Entry Date</h2>
                                                <p>
                                                    This permit is valid for a single date
                                                    between {start.utc().format('LL')} to{' '}
                                                    {end.utc().format('LL')}. You will
                                                    specify your exact entry date when you
                                                    get your permit issued at the
                                                    facility.
                                                </p>
                                            </div>
                                            <hr />
                                        </div>
                                    )}
                                    <GuideDetails
                                        issuance={this.props.issuance}
                                        isDisabled={
                                            this.props.issuance.status ===
                                            issuanceStatuses.get(types.PERMIT_ISSUED)
                                        }
                                        handleIsGuidedChange={this.handleIsGuidedChange}
                                        handleGuideFirstNameChange={
                                            this.handleGuideFirstNameChange
                                        }
                                        handleGuideLastNameChange={
                                            this.handleGuideLastNameChange
                                        }
                                        handleGuideNumberChange={
                                            this.handleGuideNumberChange
                                        }
                                        formValidationChange={this.formValidationChange}
                                        validation={this.props.validation}
                                        displayValidationError={this.state.displayError}
                                    />
                                    <NonProfitOrg
                                        issuance={this.props.issuance}
                                        isDisabled={
                                            this.props.issuance.status ===
                                            issuanceStatuses.get(types.PERMIT_ISSUED)
                                        }
                                        handleIsNonProfitOrgChange={
                                            this.handleIsNonProfitOrgChange
                                        }
                                        handleOrgTypeChange={this.handleOrgTypeChange}
                                        handleOrgNameChange={this.handleOrgNameChange}
                                        formValidationChange={this.formValidationChange}
                                        validation={this.props.validation}
                                        displayValidationError={this.state.displayError}
                                    />
                                    {this.renderTripDates()}
                                    <GroupMemberDetail
                                        permit={this.props.permit}
                                        division={this.state.division}
                                        groupMembers={this.state.groupMembers}
                                        altGroupLeaders={
                                            this.getGroupDetails().alt_group_leaders
                                        }
                                        maxAlternateLeaders={maxAlternateLeaders}
                                        handleGroupMemberChange={
                                            this.handleGroupMemberChange
                                        }
                                        launchDetail={this.state.launchDetail}
                                        maxMemberQuota={this.state.maxMemberQuota}
                                        validation={this.state.validation}
                                        issuance={this.props.issuance}
                                        divisionId={this.state.division?.id}
                                    />
                                    <hr />
                                    {this.renderWatercraftList()}
                                    <CanadaTravel
                                        displayValidationError={this.state.displayError}
                                        entryPointOptions={this.state.entryPointOptions}
                                        formValidationChange={this.formValidationChange}
                                        handleCanadaDaysChange={
                                            this.handleCanadaDaysChange
                                        }
                                        handleCanadaEntryPointChange={
                                            this.handleCanadaEntryPointChange
                                        }
                                        handleCanadaTravelChange={
                                            this.handleCanadaTravelChange
                                        }
                                        isDisabled={
                                            this.props.issuance.status ===
                                            issuanceStatuses.get(types.PERMIT_ISSUED)
                                        }
                                        isEntryPointEnabled={
                                            this.state.entryPointOptions.length > 0
                                        }
                                        issuance={this.props.issuance}
                                        validation={this.props.validation}
                                    />
                                    <CooperatorDetails
                                        permitID={this.props.permit.id}
                                        cooperator={this.props.cooperator}
                                        reservationID={
                                            this.props.router.params.reservationIds
                                        }
                                        onChangeCooperator={(e) => {
                                            e.preventDefault();
                                            this.onChangeCooperator();
                                        }}
                                        isDisabled={
                                            this.props.issuance.status ===
                                                issuanceStatuses.get(
                                                    types.PERMIT_ISSUED
                                                ) ||
                                            (!this.props.flags
                                                ?.permitBwAllowCooperatorMod &&
                                                this.props.issuance.status ===
                                                    issuanceStatuses.get(
                                                        types.PERMIT_COMPLETE
                                                    ))
                                        }
                                    />
                                    <NeedToKnow
                                        permitName={this.props.permit.name}
                                        issuance={this.props.issuance}
                                        needToKnow={
                                            this.props.permit.description
                                                ?.need_to_know_order
                                        }
                                        checked={this.state.needToKnowChecked}
                                        toggleCheckbox={
                                            this.handleNeedToKnowCheckboxChange
                                        }
                                        formValidationChange={this.formValidationChange}
                                        validation={this.props.validation}
                                        displayValidationError={this.state.displayError}
                                    />
                                </fieldset>
                            </form>
                        </OrderDetailsSection>
                    </OrderDetailsLayout>
                </ContentBlock>
            </div>
        );
    }
}

function select(state) {
    return {
        permit: state.boundarywaters.permit.result,
        imageUrl: sharedSelectors.getImageUrlSelector(state),
        permitType: state.boundarywaters.permitType,
        issuance: state.boundarywaters.permit.issuance,
        issuanceList: state.boundarywaters.permit.issuanceList,
        issuanceError: state.boundarywaters.permit.error,
        updateIssuanceResult: state.boundarywaters.update_issuance_result,
        updatedIssuance: state.boundarywaters.updated_issuance,
        cooperator: state.boundarywaters.permit.cooperator,
        recareaError: state.boundarywaters.permit.recarea_err,
        fetchError: state.boundarywaters.permit.fetch_err,
        validation: state.boundarywaters.validation,
        cart: state.boundarywaters.permit.cart,
        cartError: state.boundarywaters.permit.cart_err,
        filters: state.boundarywaters.permit.filters,
        issuanceLoading: state.boundarywaters.issuanceLoading,
    };
}

export default withCompatRouter(
    withLDConsumer()(connect(select)(PermitRegistrationPage))
);

// cSpell:ignore boundarywaters, recarea, recareas, passholder, bwcaw
